import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { escapeHtml } from './textEditorBodyUtils';
import {
  enrichHtmlForDocumentExport,
  mountAndInlinePresentationStyles,
} from './textEditorExportEnrich';
import { buildDocxBlobFromExportHtml } from './textEditorExportDocx';
import {
  getTextEditorPageLayoutMetrics,
} from './textEditorPageOrientation';
import {
  resolveTextEditorPageExportSettings,
} from './textEditorPageLayout';
import {
  buildExportPrintLayoutCss,
  buildWordSectionPrintPaddingCss,
  wrapExportBodyForPrintLayout,
} from './textEditorPrintLayout';

const EXPORT_STYLES = `
body {
  font-family: Calibri, "Segoe UI", Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.45;
  color: #111827;
  margin: 0;
  padding: 0;
}
img, video { max-width: 100%; height: auto; }
table { border-collapse: collapse; width: 100%; margin: 0.5rem 0; }
th, td { border: 1px solid #94a3b8; padding: 6px 8px; vertical-align: top; }
thead th { font-weight: 600; background: #f1f5f9; }
ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
li { display: list-item; margin: 0.25rem 0; }
a { color: #1a73e8; }
pre, code { font-family: Consolas, "Courier New", monospace; font-size: 10pt; }
pre { background: #f8fafc; padding: 0.65rem 0.75rem; border-radius: 6px; overflow-x: auto; }
blockquote { border-left: 3px solid #cbd5e1; margin: 0.75rem 0; padding-left: 0.75rem; color: #374151; }
h1, h2, h3, h4, h5, h6 { font-weight: 600; margin: 0.75rem 0 0.35rem; }
p { margin: 0 0 0.5rem; }
hr { border: 0; border-top: 1px solid #cbd5e1; margin: 0.75rem 0; }
`;

function buildWordExtraStyles(pageOrientation, pageNumbers, printMargins) {
  const metrics = getTextEditorPageLayoutMetrics(pageOrientation);
  const m = printMargins;
  const footerRule = pageNumbers
    ? `
div.Section1 footer, div.Section1 .MsoFooter, p.MsoFooter {
  text-align: center;
  font-size: 10pt;
  color: #64748b;
}
`
    : '';
  return `
@page Section1 { size: ${metrics.wordPageSizeCss}; margin: ${m.topCss} ${m.rightCss} ${m.bottomCss} ${m.leftCss}; }
div.Section1 { page: Section1; }
${buildWordSectionPrintPaddingCss(m)}
${footerRule}
`;
}

function buildWordPageFooterHtml() {
  return `
<div style="mso-element:footer" id="wf1">
  <p align="center" class="MsoFooter" style="text-align:center;font-size:10pt;color:#64748b;">
    Page <!--[if supportFields]><span style="mso-field-code:&quot; PAGE &quot;"></span><![endif]-->
  </p>
</div>`;
}

export function sanitizeExportBaseName(name) {
  const base = String(name || 'document')
    .replace(/\.[^./\\]+$/i, '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/[\0\r\n]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  return base || 'document';
}

function triggerDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function resolveExportOptions(exportOptions = {}) {
  const resolved = resolveTextEditorPageExportSettings({
    pageOrientation: exportOptions.pageOrientation,
    pageLayoutMode: exportOptions.pageLayoutMode,
    printMarginPreset: exportOptions.printMarginPreset,
    customPrintMarginsIn: exportOptions.customPrintMarginsIn,
    printPageNumbersEnabled: exportOptions.printPageNumbersEnabled,
  });
  return {
    liveRoot: exportOptions.liveRoot ?? null,
    pageOrientation: resolved.pageOrientation,
    pageLayoutMode: resolved.pageLayoutMode,
    pageNumbers: resolved.pageNumbers,
    printMarginPreset: resolved.printMarginPreset,
    customPrintMarginsIn: resolved.customPrintMarginsIn,
    printMargins: resolved.printMargins,
    printPageNumbersEnabled: exportOptions.printPageNumbersEnabled,
  };
}

function pickTextEditorExportOptions(source = {}) {
  return {
    liveRoot: source.liveRoot,
    pageOrientation: source.pageOrientation,
    pageLayoutMode: source.pageLayoutMode,
    printMarginPreset: source.printMarginPreset,
    customPrintMarginsIn: source.customPrintMarginsIn,
    printPageNumbersEnabled: source.printPageNumbersEnabled,
  };
}

function prepareExportBodyHtml(bodyHtml, exportOptions = {}) {
  let working = bodyHtml || '';
  const { liveRoot } = resolveExportOptions(exportOptions);
  if (liveRoot?.parentElement) {
    working = mountAndInlinePresentationStyles(
      working,
      liveRoot.className,
      liveRoot.parentElement
    );
  }
  return enrichHtmlForDocumentExport(working);
}

export function buildStandaloneHtmlDocument(bodyHtml, title = 'Document', exportOptions = {}) {
  const safeTitle = escapeHtml(title);
  const resolved = resolveExportOptions(exportOptions);
  const enriched = prepareExportBodyHtml(bodyHtml, exportOptions);
  const wrapped = wrapExportBodyForPrintLayout(enriched);
  const printLayoutCss = buildExportPrintLayoutCss(
    resolved.pageOrientation,
    resolved.pageNumbers,
    resolved.printMargins
  );
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${safeTitle}</title>
<style>${EXPORT_STYLES}${printLayoutCss}</style>
</head>
<body>${wrapped}</body>
</html>`;
}

export function buildWordCompatibleHtmlDocument(bodyHtml, title = 'Document', exportOptions = {}) {
  const safeTitle = escapeHtml(title);
  const resolved = resolveExportOptions(exportOptions);
  const enriched = prepareExportBodyHtml(bodyHtml, resolved);
  const wordExtraStyles = buildWordExtraStyles(
    resolved.pageOrientation,
    resolved.pageNumbers,
    resolved.printMargins
  );
  const wordFooter = resolved.pageNumbers ? buildWordPageFooterHtml() : '';
  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:w="urn:schemas-microsoft-com:office:word"
  xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<meta name="ProgId" content="Word.Document" />
<meta name="Generator" content="Microsoft Word" />
<title>${safeTitle}</title>
<!--[if gte mso 9]><xml>
<w:WordDocument>
  <w:View>Print</w:View>
  <w:Zoom>100</w:Zoom>
  <w:DoNotOptimizeForBrowser/>
</w:WordDocument>
</xml><![endif]-->
<style>
${EXPORT_STYLES}
${wordExtraStyles}
</style>
</head>
<body>
<div class="Section1">${wrapExportBodyForPrintLayout(enriched)}${wordFooter}</div>
</body>
</html>`;
}

export function exportTextEditorAsHtml({
  bodyHtml,
  baseName,
  title,
  liveRoot,
  pageOrientation,
  pageLayoutMode,
  printMarginPreset,
  customPrintMarginsIn,
  printPageNumbersEnabled,
}) {
  const name = sanitizeExportBaseName(baseName);
  const exportOptions = pickTextEditorExportOptions({
    liveRoot,
    pageOrientation,
    pageLayoutMode,
    printMarginPreset,
    customPrintMarginsIn,
    printPageNumbersEnabled,
  });
  const doc = buildStandaloneHtmlDocument(bodyHtml, title || name, exportOptions);
  const blob = new Blob([doc], { type: 'text/html;charset=utf-8' });
  triggerDownload(blob, `${name}.html`);
}

export function exportTextEditorAsWordDoc({
  bodyHtml,
  baseName,
  title,
  liveRoot,
  pageOrientation,
  pageLayoutMode,
  printMarginPreset,
  customPrintMarginsIn,
  printPageNumbersEnabled,
}) {
  const name = sanitizeExportBaseName(baseName);
  const exportOptions = pickTextEditorExportOptions({
    liveRoot,
    pageOrientation,
    pageLayoutMode,
    printMarginPreset,
    customPrintMarginsIn,
    printPageNumbersEnabled,
  });
  const doc = buildWordCompatibleHtmlDocument(bodyHtml, title || name, exportOptions);
  const blob = new Blob(['\ufeff', doc], { type: 'application/msword;charset=utf-8' });
  triggerDownload(blob, `${name}.doc`);
}

export async function exportTextEditorAsDocx({
  bodyHtml,
  baseName,
  title,
  liveRoot,
  pageOrientation,
  pageLayoutMode,
  printMarginPreset,
  customPrintMarginsIn,
  printPageNumbersEnabled,
}) {
  const name = sanitizeExportBaseName(baseName);
  const exportOptions = pickTextEditorExportOptions({
    liveRoot,
    pageOrientation,
    pageLayoutMode,
    printMarginPreset,
    customPrintMarginsIn,
    printPageNumbersEnabled,
  });
  const resolved = resolveExportOptions(exportOptions);
  const enriched = prepareExportBodyHtml(bodyHtml, exportOptions);
  const blob = await buildDocxBlobFromExportHtml(
    enriched,
    title || name,
    resolved.pageOrientation,
    { pageNumbers: resolved.pageNumbers, printMargins: resolved.printMargins }
  );
  triggerDownload(blob, `${name}.docx`);
}

function getPdfRenderWidthPx(pageOrientation) {
  return getTextEditorPageLayoutMetrics(pageOrientation).widthPx;
}

function waitForImages(root) {
  const images = Array.from(root.querySelectorAll('img'));
  if (!images.length) return Promise.resolve();
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  );
}

async function renderExportHtmlToCanvas(bodyHtml, title, exportOptions = {}) {
  const options = pickTextEditorExportOptions(exportOptions);
  const resolved = resolveExportOptions(options);
  const renderWidthPx = getPdfRenderWidthPx(resolved.pageOrientation);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${renderWidthPx}px`,
    'height:1px',
    'border:0',
    'visibility:hidden',
  ].join(';');
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    doc.open();
    doc.write(buildStandaloneHtmlDocument(bodyHtml, title, options));
    doc.close();

    await new Promise((resolve) => {
      iframe.onload = () => resolve();
      window.setTimeout(resolve, 400);
    });

    const body = doc.body;
    await waitForImages(body);
    iframe.style.height = `${Math.max(body.scrollHeight, 400)}px`;

    await new Promise((r) => window.requestAnimationFrame(() => window.requestAnimationFrame(r)));

    return await html2canvas(body, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: renderWidthPx,
      width: renderWidthPx,
      height: body.scrollHeight,
    });
  } finally {
    iframe.remove();
  }
}

export async function exportTextEditorAsPdf({
  bodyHtml,
  baseName,
  title,
  liveRoot,
  pageOrientation,
  pageLayoutMode,
  printMarginPreset,
  customPrintMarginsIn,
  printPageNumbersEnabled,
}) {
  const name = sanitizeExportBaseName(baseName);
  const exportOptions = pickTextEditorExportOptions({
    liveRoot,
    pageOrientation,
    pageLayoutMode,
    printMarginPreset,
    customPrintMarginsIn,
    printPageNumbersEnabled,
  });
  const resolved = resolveExportOptions(exportOptions);
  const metrics = getTextEditorPageLayoutMetrics(resolved.pageOrientation);
  const canvas = await renderExportHtmlToCanvas(bodyHtml, title || name, exportOptions);
  const pdf = new jsPDF({ orientation: metrics.pdfJsOrientation, unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const m = resolved.printMargins;
  const marginLeft = m.leftPt;
  const marginRight = m.rightPt;
  const marginTop = m.topPt;
  const marginBottom = m.bottomPt;
  const footerBand = resolved.pageNumbers ? 20 : 0;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom - footerBand;
  const imgWidth = contentWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const imgData = canvas.toDataURL('image/jpeg', 0.92);

  let offsetY = marginTop;
  pdf.addImage(imgData, 'JPEG', marginLeft, offsetY, imgWidth, imgHeight);
  let remaining = imgHeight - contentHeight;

  while (remaining > 0) {
    offsetY -= contentHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'JPEG', marginLeft, offsetY, imgWidth, imgHeight);
    remaining -= contentHeight;
  }

  if (resolved.pageNumbers) {
    const totalPages = pdf.getNumberOfPages();
    for (let pageIndex = 1; pageIndex <= totalPages; pageIndex += 1) {
      pdf.setPage(pageIndex);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(100, 116, 139);
      pdf.text(`Page ${pageIndex} of ${totalPages}`, pageWidth / 2, pageHeight - marginBottom / 2, {
        align: 'center',
      });
    }
  }

  pdf.save(`${name}.pdf`);
}

export async function runTextEditorExport(format, {
  bodyHtml,
  baseName,
  title,
  liveRoot,
  pageOrientation,
  pageLayoutMode,
  printMarginPreset,
  customPrintMarginsIn,
  printPageNumbersEnabled,
}) {
  const exportPayload = {
    bodyHtml,
    baseName,
    title,
    liveRoot,
    pageOrientation,
    pageLayoutMode,
    printMarginPreset,
    customPrintMarginsIn,
    printPageNumbersEnabled,
  };
  if (format === 'html') {
    exportTextEditorAsHtml(exportPayload);
    return { ok: true };
  }
  if (format === 'word' || format === 'doc') {
    exportTextEditorAsWordDoc(exportPayload);
    return { ok: true };
  }
  if (format === 'docx') {
    await exportTextEditorAsDocx(exportPayload);
    return { ok: true };
  }
  if (format === 'pdf') {
    await exportTextEditorAsPdf(exportPayload);
    return { ok: true };
  }
  return { ok: false };
}

/** Opens the system print dialog with layout matching export/print CSS. */
export function runTextEditorPrint({
  bodyHtml,
  title,
  liveRoot,
  pageOrientation,
  pageLayoutMode,
  printMarginPreset,
  customPrintMarginsIn,
  printPageNumbersEnabled,
}) {
  let docHtml;
  try {
    docHtml = buildStandaloneHtmlDocument(bodyHtml, title || 'Document', {
      liveRoot,
      pageOrientation,
      pageLayoutMode,
      printMarginPreset,
      customPrintMarginsIn,
      printPageNumbersEnabled,
    });
  } catch (err) {
    window.alert(err?.message || 'Could not prepare this document for printing.');
    return;
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', title || 'Print document');
  iframe.className = 'wfm-text-editor__print-frame';
  iframe.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;border:0;z-index:2147483647;background:#fff;';
  document.body.appendChild(iframe);

  const frameWin = iframe.contentWindow;
  const frameDoc = frameWin?.document;
  if (!frameWin || !frameDoc) {
    iframe.remove();
    window.alert('Could not open the print dialog.');
    return;
  }

  try {
    frameDoc.open();
    frameDoc.write(docHtml);
    frameDoc.close();
  } catch (err) {
    iframe.remove();
    window.alert(err?.message || 'Could not open the print dialog.');
    return;
  }

  let printed = false;
  const cleanup = () => {
    window.setTimeout(() => {
      try {
        iframe.remove();
      } catch {
        /* ignore */
      }
    }, 400);
  };

  const invokeSystemPrint = () => {
    if (printed) return;
    printed = true;
    try {
      frameWin.focus();
      frameWin.print();
    } catch (err) {
      iframe.remove();
      window.alert(err?.message || 'Could not open the print dialog.');
      return;
    }
    if (typeof frameWin.addEventListener === 'function') {
      frameWin.addEventListener('afterprint', cleanup, { once: true });
    }
    window.setTimeout(cleanup, 120_000);
  };

  if (frameDoc.readyState === 'complete') {
    window.setTimeout(invokeSystemPrint, 150);
  } else {
    iframe.onload = () => window.setTimeout(invokeSystemPrint, 150);
    window.setTimeout(invokeSystemPrint, 800);
  }
}
